import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PHASES, ASSETS } from "../data/content";

export default function Phases() {
    const [active, setActive] = useState(0);
    const phase = PHASES[active];

    return (
        <section
            id="phases"
            data-testid="phases-section"
            className="relative py-24 sm:py-36"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-10">
                <div className="text-center mb-16">
                    <div className="overline mb-5">The System · Five Phases</div>
                    <h2
                        className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#f4f4f0]"
                        data-testid="phases-title"
                    >
                        From <span className="italic text-neutral-500">prey</span>
                        {" · to · "}
                        <span className="italic text-[#990000]">predator</span>.
                    </h2>
                    <p className="mt-6 max-w-2xl mx-auto text-neutral-400">
                        A staged transformation. No theory. No filler. Each
                        phase dismantles a different layer of the man you used
                        to be.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                    {/* Phase selector */}
                    <div
                        className="lg:col-span-5 pb-24 lg:pb-0"
                        data-testid="phases-tablist"
                        role="tablist"
                    >
                        {PHASES.map((p, i) => (
                            <button
                                key={p.roman}
                                role="tab"
                                aria-selected={active === i}
                                data-active={active === i}
                                data-testid={`phase-tab-${i}`}
                                onClick={() => setActive(i)}
                                className="phase-trigger group"
                            >
                                <div className="flex items-baseline gap-5">
                                    <span className="font-serif-display italic text-2xl text-[#7A0000] w-10">
                                        {p.roman}
                                    </span>
                                    <div className="flex-1">
                                        <div className="font-serif-display text-xl sm:text-2xl leading-tight">
                                            {p.title}
                                        </div>
                                        <div className="mt-1 text-xs tracking-[0.24em] uppercase text-neutral-600">
                                            Phase 0{i + 1}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Phase content */}
                    <div className="lg:col-span-7">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active}
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{
                                    duration: 0.6,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                data-testid={`phase-content-${active}`}
                                className="relative bg-[#0A0A0A] border border-white/5 p-8 sm:p-12"
                            >
                                <div
                                    className="absolute -top-px left-0 w-20 h-px"
                                    style={{
                                        background:
                                            "linear-gradient(90deg, #7A0000, transparent)",
                                    }}
                                />
                                <div className="overline mb-6">
                                    Phase {phase.roman} · Excerpt
                                </div>
                                <h3 className="font-serif-display italic text-3xl sm:text-4xl text-[#f4f4f0] leading-tight">
                                    {phase.title}
                                </h3>
                                <p className="mt-6 text-neutral-300 text-base sm:text-lg leading-relaxed">
                                    {phase.teaser}
                                </p>
                                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    {phase.bullets.map((b, j) => (
                                        <div
                                            key={j}
                                            className="border-l border-[#7A0000]/40 pl-4 py-2"
                                        >
                                            <div className="text-[10px] tracking-[0.32em] uppercase text-[#990000] mb-1">
                                                Lesson · 0{j + 1}
                                            </div>
                                            <div className="text-sm text-neutral-200 font-serif-display italic text-lg">
                                                {b}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-10 flex items-center justify-between text-xs tracking-[0.22em] uppercase text-neutral-500">
                                    <div>Sealed chapter — preview only</div>
                                    <a
                                        href="#pricing"
                                        data-testid={`phase-unlock-${active}`}
                                        className="text-[#f4f4f0] hover:text-[#990000] transition-colors"
                                    >
                                        Unlock the full phase →
                                    </a>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
}
